import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { FileText, Grip, Maximize2, PictureInPicture2, X } from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { getDocumentMediaKind } from "@/features/document-viewport/media";
import type { DocumentSource } from "@/features/document-viewport/types";
import {
  DocumentViewportPageControls,
  DocumentViewportToolbar,
  DocumentViewportToolbarSection,
} from "@/features/document-viewport/toolbar";
import { cn } from "@/lib/utils";

/** PDF file reference consumed by the modal/PiP viewer. `fileUrl` may be null while unavailable. */
export interface PdfModalPipSourceRef {
  fileUrl: string | null;
  filename: string;
}

/** Low-level viewport renderer; callers may provide page state to preserve context across mounts. */
export interface PdfModalPipViewportProps {
  sourcePdf?: PdfModalPipSourceRef | null;
  sourceId: string;
  initialPage?: number;
  activePage?: number;
  onActivePageChange?: (page: number) => void;
  /** Labels shown with page numbers in the thumbnails and pager. */
  pageLabels?: ReadonlyMap<number, string>;
  className?: string;
  toolbarActions?: ReactNode;
}

/** `modal` traps focus in a near-fullscreen dialog; `pip` floats above the page without blocking edits. */
export type PdfModalPipMode = "modal" | "pip";

/** Reusable PDF launcher. `sourceId` should be stable for a given `sourcePdf` to reset viewport state correctly. */
export interface PdfModalPipProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourcePdf?: PdfModalPipSourceRef | null;
  sourceId: string;
  initialMode?: PdfModalPipMode;
  /** Page shown each time the viewer opens. */
  initialPage?: number;
}

interface PdfModalPipModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourcePdf?: PdfModalPipSourceRef | null;
  sourceId: string;
  activePage: number;
  onActivePageChange: (page: number) => void;
  onPopOut: () => void;
}

interface PdfModalPipViewerProps {
  open: boolean;
  sourcePdf?: PdfModalPipSourceRef | null;
  sourceId: string;
  activePage: number;
  onActivePageChange: (page: number) => void;
  onClose: () => void;
  onOpenFullscreen: () => void;
}

interface FloatingBounds {
  width: number;
  height: number;
  x: number;
  y: number;
}

type FloatingInteraction =
  | {
      type: "drag";
      startX: number;
      startY: number;
      initialX: number;
      initialY: number;
    }
  | {
      type: "resize";
      startX: number;
      startY: number;
      initialWidth: number;
      initialHeight: number;
    };

const MIN_PIP_WIDTH = 360;
const MIN_PIP_HEIGHT = 280;
const PIP_VIEWPORT_MARGIN = 16;

function clampFloatingBounds(bounds: FloatingBounds): FloatingBounds {
  if (typeof window === "undefined") {
    return bounds;
  }

  const maxWidth = Math.max(1, window.innerWidth - PIP_VIEWPORT_MARGIN * 2);
  const maxHeight = Math.max(1, window.innerHeight - PIP_VIEWPORT_MARGIN * 2);
  const minWidth = Math.min(MIN_PIP_WIDTH, maxWidth);
  const minHeight = Math.min(MIN_PIP_HEIGHT, maxHeight);
  const width = Math.min(maxWidth, Math.max(minWidth, bounds.width));
  const height = Math.min(maxHeight, Math.max(minHeight, bounds.height));
  return {
    width,
    height,
    x: Math.min(
      Math.max(PIP_VIEWPORT_MARGIN, bounds.x),
      Math.max(PIP_VIEWPORT_MARGIN, window.innerWidth - width - PIP_VIEWPORT_MARGIN)
    ),
    y: Math.min(
      Math.max(PIP_VIEWPORT_MARGIN, bounds.y),
      Math.max(PIP_VIEWPORT_MARGIN, window.innerHeight - height - PIP_VIEWPORT_MARGIN)
    ),
  };
}

function getInitialFloatingBounds(): FloatingBounds {
  if (typeof window === "undefined") {
    return { width: 560, height: 420, x: 24, y: 24 };
  }

  const width = Math.min(620, Math.max(MIN_PIP_WIDTH, window.innerWidth - 48));
  const height = Math.min(520, Math.max(MIN_PIP_HEIGHT, window.innerHeight - 48));
  return clampFloatingBounds({
    width,
    height,
    x: window.innerWidth - width - 24,
    y: window.innerHeight - height - 24,
  });
}

export function PdfModalPipViewport({
  sourcePdf,
  sourceId,
  initialPage = 1,
  activePage: controlledActivePage,
  onActivePageChange,
  pageLabels,
  className,
  toolbarActions,
}: PdfModalPipViewportProps) {
  const [uncontrolledActivePage, setUncontrolledActivePage] = useState(Math.max(1, initialPage));
  const [pageCount, setPageCount] = useState(1);
  const activePage = controlledActivePage ?? uncontrolledActivePage;

  const setActivePage = useCallback(
    (page: number) => {
      const nextPage = Math.max(1, page);
      setUncontrolledActivePage(nextPage);
      onActivePageChange?.(nextPage);
    },
    [onActivePageChange]
  );

  const handlePageCountChange = useCallback(
    (nextPageCount: number) => {
      setPageCount(nextPageCount);
      if (nextPageCount > 0 && activePage > nextPageCount) {
        setActivePage(nextPageCount);
      }
    },
    [activePage, setActivePage]
  );

  useEffect(() => {
    if (controlledActivePage === undefined) {
      setActivePage(Math.max(1, Math.min(initialPage, pageCount)));
    }
  }, [controlledActivePage, initialPage, pageCount, setActivePage]);

  const source = useMemo<DocumentSource | null>(() => {
    if (!sourcePdf?.fileUrl) return null;

    return {
      revisionId: sourceId,
      fileUrl: sourcePdf.fileUrl,
      filename: sourcePdf.filename,
      mimeType: "application/pdf",
      mediaKind: getDocumentMediaKind(sourcePdf.filename, "application/pdf"),
    };
  }, [sourceId, sourcePdf]);

  if (sourcePdf === undefined) {
    return (
      <div
        className={cn(
          "flex min-h-[320px] items-center justify-center rounded-lg border-2 bg-muted/20 p-6 text-center text-sm text-muted-foreground",
          className
        )}
      >
        Loading source PDF...
      </div>
    );
  }

  if (!source) {
    return (
      <div
        className={cn(
          "flex min-h-[320px] items-center justify-center rounded-lg border-2 bg-muted/20 p-6 text-center text-sm text-muted-foreground",
          className
        )}
      >
        Source PDF is unavailable.
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative min-h-[320px] overflow-hidden rounded-lg border-2 bg-background",
        className
      )}
    >
      <DocumentViewportHost
        source={source}
        activePage={activePage}
        onActivePageChange={setActivePage}
        onPageCountChange={handlePageCountChange}
        pageLabels={pageLabels}
        viewportPadding={{ top: 72, right: 24, bottom: 24, left: 24 }}
        className="absolute inset-0 overflow-hidden"
        toolbar={
          <DocumentViewportToolbar>
            <DocumentViewportToolbarSection className="min-w-0 flex-1 overflow-hidden">
              <FileText className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate text-xs font-semibold">
                {source.filename}
              </span>
            </DocumentViewportToolbarSection>
            <div className="shrink-0">
              <DocumentViewportPageControls
                activePage={activePage}
                pageCount={pageCount}
              />
            </div>
            {toolbarActions ? (
              <DocumentViewportToolbarSection className="shrink-0">
                {toolbarActions}
              </DocumentViewportToolbarSection>
            ) : null}
          </DocumentViewportToolbar>
        }
      />
    </div>
  );
}

function PdfModalPipModal({
  open,
  onOpenChange,
  sourcePdf,
  sourceId,
  onPopOut,
  activePage,
  onActivePageChange,
}: PdfModalPipModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-[95dvh] max-h-[95dvh] w-[95vw] max-w-[95vw] overflow-hidden p-0 sm:max-w-[95vw]"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">View source PDF</DialogTitle>
        <PdfModalPipViewport
          sourcePdf={sourcePdf}
          sourceId={sourceId}
          activePage={activePage}
          onActivePageChange={onActivePageChange}
          className="h-full min-h-0 rounded-none border-0"
          toolbarActions={
            <>
              <IconButton label="Pop out PDF viewer" icon={PictureInPicture2} onClick={onPopOut} />
              <IconButton
                label="Close PDF viewer"
                icon={X}
                onClick={() => onOpenChange(false)}
              />
            </>
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function PdfModalPipViewer({
  open,
  sourcePdf,
  sourceId,
  onClose,
  onOpenFullscreen,
  activePage,
  onActivePageChange,
}: PdfModalPipViewerProps) {
  const [bounds, setBounds] = useState<FloatingBounds>(getInitialFloatingBounds);
  const interactionRef = useRef<FloatingInteraction | null>(null);

  const moveBy = useCallback((deltaX: number, deltaY: number) => {
    setBounds((current) =>
      clampFloatingBounds({
        ...current,
        x: current.x + deltaX,
        y: current.y + deltaY,
      })
    );
  }, []);

  const resizeBy = useCallback((deltaWidth: number, deltaHeight: number) => {
    setBounds((current) =>
      clampFloatingBounds({
        ...current,
        width: current.width + deltaWidth,
        height: current.height + deltaHeight,
      })
    );
  }, []);

  const handleMoveKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 40 : 10;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveBy(0, -step);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      moveBy(0, step);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveBy(-step, 0);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      moveBy(step, 0);
    }
  }, [moveBy]);

  const handleResizeKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 40 : 10;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      resizeBy(0, -step);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      resizeBy(0, step);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      resizeBy(-step, 0);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      resizeBy(step, 0);
    }
  }, [resizeBy]);

  const startDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    interactionRef.current = {
      type: "drag",
      startX: event.clientX,
      startY: event.clientY,
      initialX: bounds.x,
      initialY: bounds.y,
    };
  }, [bounds.x, bounds.y]);

  const startResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    interactionRef.current = {
      type: "resize",
      startX: event.clientX,
      startY: event.clientY,
      initialWidth: bounds.width,
      initialHeight: bounds.height,
    };
  }, [bounds.height, bounds.width]);

  useEffect(() => {
    if (!open) return;

    const handlePointerMove = (event: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction) return;

      if (interaction.type === "drag") {
        setBounds((current) =>
          clampFloatingBounds({
            ...current,
            x: interaction.initialX + event.clientX - interaction.startX,
            y: interaction.initialY + event.clientY - interaction.startY,
          })
        );
        return;
      }

      setBounds((current) =>
        clampFloatingBounds({
          ...current,
          width: interaction.initialWidth + event.clientX - interaction.startX,
          height: interaction.initialHeight + event.clientY - interaction.startY,
        })
      );
    };

    const stopInteraction = () => {
      interactionRef.current = null;
    };

    const clampOnResize = () => {
      setBounds((current) => clampFloatingBounds(current));
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopInteraction);
    window.addEventListener("resize", clampOnResize);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopInteraction);
      window.removeEventListener("resize", clampOnResize);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed z-[120] overflow-hidden rounded-lg border-2 bg-background shadow-2xl"
      style={{
        width: bounds.width,
        height: bounds.height,
        left: bounds.x,
        top: bounds.y,
      }}
    >
      <PdfModalPipViewport
        sourcePdf={sourcePdf}
        sourceId={sourceId}
        activePage={activePage}
        onActivePageChange={onActivePageChange}
        className="h-full min-h-0 rounded-none border-0"
        toolbarActions={
          <>
            <div
              className="flex size-8 cursor-move touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted/70"
              onPointerDown={startDrag}
              onKeyDown={handleMoveKeyDown}
              role="button"
              tabIndex={0}
              aria-label="Move PDF viewer"
              title="Move PDF viewer"
            >
              <Grip className="size-4" />
            </div>
            <IconButton
              label="Open PDF viewer fullscreen"
              icon={Maximize2}
              onClick={onOpenFullscreen}
            />
            <IconButton label="Close PDF viewer" icon={X} onClick={onClose} />
          </>
        }
      />
      <div
        className="absolute bottom-1 right-1 z-[80] flex size-6 cursor-se-resize touch-none items-center justify-center rounded-md bg-background/85 text-muted-foreground shadow-sm ring-1 ring-border/70 hover:bg-muted"
        onPointerDownCapture={startResize}
        onKeyDown={handleResizeKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Resize PDF viewer"
        title="Resize PDF viewer"
      >
        <Grip className="size-3.5 rotate-45" />
      </div>
    </div>
  );
}

export function PdfModalPip({
  open,
  onOpenChange,
  sourcePdf,
  sourceId,
  initialMode = "modal",
  initialPage = 1,
}: PdfModalPipProps) {
  const [mode, setMode] = useState<PdfModalPipMode>(initialMode);
  const [activePage, setActivePage] = useState(initialPage);
  const [pageSource, setPageSource] = useState({ sourceId, initialPage });
  const wasOpenRef = useRef(false);
  const displayMode = open && !wasOpenRef.current ? initialMode : mode;

  // A new source mapping (e.g. another packet page) restarts at its page; in-source navigation is kept.
  if (pageSource.sourceId !== sourceId || pageSource.initialPage !== initialPage) {
    setPageSource({ sourceId, initialPage });
    setActivePage(initialPage);
  }

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setMode(initialMode);
      setActivePage(initialPage);
    }
    wasOpenRef.current = open;
  }, [initialMode, initialPage, open]);

  if (!open) return null;

  return (
    <>
      <PdfModalPipModal
        open={displayMode === "modal"}
        onOpenChange={onOpenChange}
        sourcePdf={sourcePdf}
        sourceId={sourceId}
        activePage={activePage}
        onActivePageChange={setActivePage}
        onPopOut={() => setMode("pip")}
      />
      <PdfModalPipViewer
        open={displayMode === "pip"}
        sourcePdf={sourcePdf}
        sourceId={sourceId}
        activePage={activePage}
        onActivePageChange={setActivePage}
        onClose={() => onOpenChange(false)}
        onOpenFullscreen={() => setMode("modal")}
      />
    </>
  );
}
